import { ReactNode, useEffect, useMemo, useState } from "react";
import axios from "../../../../services/axios";
import { useFilter } from "../../../../hooks/useFilter";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import { ButtonOnlyTitle } from "../../../Buttons/ButtonOnlyTitle/Index";
import { DropdownV2, InputV2, OptionV2 } from "../../../FormControlsV2";
import { AlertModal } from "../../../Modal/Alert/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { ReactComponent as CheckIcon } from "../../../../assets/svg/icons/CheckFull.svg";
import "./styles.css";

type CatalogProduct = {
  id: number;
  trade_name: string;
  manufacturer?: string | null;
  classification?: string | null;
};

type StockProduct = {
  product_id: number;
  quantity: string;
};

type PastureStock = {
  micro_area_id: number;
  products: StockProduct[];
};

type Inventory = {
  central_storage: StockProduct[];
  pasture_storage: PastureStock[];
};

type ApiInventory = {
  central_storage?: Array<{ product_id: number; quantity: number }>;
  pasture_storage?: Array<{
    micro_area_id: number;
    products?: Array<{ product_id: number; quantity: number }>;
  }>;
};

type MicroArea = {
  id: number;
  initials?: string;
  area?: { initials?: string };
  farm?: { initials?: string };
};

const emptyInventory: Inventory = { central_storage: [], pasture_storage: [] };

function normalizeInventory(data?: ApiInventory): Inventory {
  return removeZeroQuantityProducts({
    central_storage: (data?.central_storage || []).map((item) => ({
      product_id: Number(item.product_id),
      quantity: String(item.quantity ?? 0),
    })),
    pasture_storage: (data?.pasture_storage || []).map((storage) => ({
      micro_area_id: Number(storage.micro_area_id),
      products: (storage.products || []).map((item) => ({
        product_id: Number(item.product_id),
        quantity: String(item.quantity ?? 0),
      })),
    })),
  });
}

function removeZeroQuantityProducts(inventory: Inventory): Inventory {
  return {
    central_storage: inventory.central_storage.filter(
      (item) => Number(item.quantity) !== 0,
    ),
    pasture_storage: inventory.pasture_storage
      .map((storage) => ({
        ...storage,
        products: storage.products.filter(
          (item) => Number(item.quantity) !== 0,
        ),
      }))
      .filter((storage) => storage.products.length > 0),
  };
}

type StockManagementProps = {
  selectedFarm: OptionV2<number> | null;
  onDirtyChange: (hasChanges: boolean) => void;
  mobileHeaderAction?: ReactNode;
};

export function StockManagement({
  selectedFarm,
  onDirtyChange,
  mobileHeaderAction,
}: StockManagementProps) {
  const { setSystemManager } = useFilter();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [microAreas, setMicroAreas] = useState<MicroArea[]>([]);
  const [inventory, setInventory] = useState<Inventory>(emptyInventory);
  const [initialInventory, setInitialInventory] =
    useState<Inventory>(emptyInventory);
  const [enabledPastures, setEnabledPastures] = useState<number[]>([]);
  const [pendingPasture, setPendingPasture] = useState<OptionV2<number> | null>(
    null,
  );
  const [pendingProducts, setPendingProducts] = useState<
    Record<string, OptionV2<number> | null>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  const productOptions = useMemo<OptionV2<number>[]>(
    () =>
      products.map((product) => ({
        label: product.trade_name,
        value: product.id,
        info: product,
      })),
    [products],
  );

  useEffect(() => {
    let active = true;
    axios.get<CatalogProduct[]>("/products/all").then(({ data }) => {
      if (active) setProducts(data || []);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedFarm) {
      setInventory(emptyInventory);
      setInitialInventory(emptyInventory);
      setMicroAreas([]);
      return;
    }

    let active = true;
    setLoading(true);
    setInventory(emptyInventory);
    setInitialInventory(emptyInventory);
    setPendingPasture(null);
    setPendingProducts({});
    Promise.all([
      axios.get<ApiInventory>(`/farms/${selectedFarm.value}/inventory`),
      axios.get<MicroArea[]>("/micro-areas", {
        params: { farm_id: selectedFarm.value },
      }),
    ])
      .then(([inventoryResponse, microAreaResponse]) => {
        if (!active) return;
        const normalized = normalizeInventory(inventoryResponse.data);
        setInventory(normalized);
        setInitialInventory(normalized);
        setMicroAreas(
          (microAreaResponse.data || []).sort((a, b) =>
            getMicroAreaLabel(a).localeCompare(getMicroAreaLabel(b)),
          ),
        );
        setEnabledPastures(
          normalized.pasture_storage
            .filter((storage) => storage.products.length > 0)
            .map((storage) => storage.micro_area_id),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedFarm]);

  useEffect(() => {
    onDirtyChange(
      JSON.stringify(inventory) !== JSON.stringify(initialInventory),
    );
  }, [initialInventory, inventory, onDirtyChange]);

  const allPastures = useMemo(() => {
    const byId = new Map<number, MicroArea>();
    microAreas.forEach((microArea) =>
      byId.set(Number(microArea.id), microArea),
    );
    inventory.pasture_storage.forEach((storage) => {
      if (!byId.has(storage.micro_area_id))
        byId.set(storage.micro_area_id, { id: storage.micro_area_id });
    });
    return Array.from(byId.values()).sort((a, b) =>
      getMicroAreaLabel(a).localeCompare(getMicroAreaLabel(b)),
    );
  }, [inventory.pasture_storage, microAreas]);

  const displayedPastures = useMemo(
    () =>
      allPastures.filter((microArea) => {
        const microAreaId = Number(microArea.id);
        const storage = inventory.pasture_storage.find(
          (item) => item.micro_area_id === microAreaId,
        );
        return (
          enabledPastures.includes(microAreaId) ||
          Boolean(storage?.products.length)
        );
      }),
    [allPastures, enabledPastures, inventory.pasture_storage],
  );

  const pastureOptions = useMemo<OptionV2<number>[]>(
    () =>
      allPastures
        .filter(
          (microArea) =>
            !displayedPastures.some(
              (displayed) => Number(displayed.id) === Number(microArea.id),
            ),
        )
        .map((microArea) => ({
          label: getMicroAreaLabel(microArea),
          value: Number(microArea.id),
          info: microArea,
        })),
    [allPastures, displayedPastures],
  );

  function addPastureStorage() {
    if (!pendingPasture) return;
    setEnabledPastures((current) =>
      current.includes(pendingPasture.value)
        ? current
        : [...current, pendingPasture.value],
    );
    setPendingPasture(null);
  }

  function updateCentralQuantity(productId: number, quantity: string) {
    setInventory((current) => ({
      ...current,
      central_storage: current.central_storage.map((item) =>
        item.product_id === productId ? { ...item, quantity } : item,
      ),
    }));
  }

  function updatePastureQuantity(
    microAreaId: number,
    productId: number,
    quantity: string,
  ) {
    setInventory((current) => ({
      ...current,
      pasture_storage: current.pasture_storage.map((storage) =>
        storage.micro_area_id === microAreaId
          ? {
              ...storage,
              products: storage.products.map((item) =>
                item.product_id === productId ? { ...item, quantity } : item,
              ),
            }
          : storage,
      ),
    }));
  }

  function addProduct(storageKey: string, microAreaId?: number) {
    const selectedProduct = pendingProducts[storageKey];
    if (!selectedProduct) return;

    setInventory((current) => {
      if (microAreaId === undefined) {
        if (
          current.central_storage.some(
            (item) => item.product_id === selectedProduct.value,
          )
        )
          return current;
        return {
          ...current,
          central_storage: [
            ...current.central_storage,
            { product_id: selectedProduct.value, quantity: "0" },
          ],
        };
      }

      const existingStorage = current.pasture_storage.find(
        (storage) => storage.micro_area_id === microAreaId,
      );
      if (!existingStorage) {
        return {
          ...current,
          central_storage: current.central_storage.some(
            (item) => item.product_id === selectedProduct.value,
          )
            ? current.central_storage
            : [
                ...current.central_storage,
                { product_id: selectedProduct.value, quantity: "0" },
              ],
          pasture_storage: [
            ...current.pasture_storage,
            {
              micro_area_id: microAreaId,
              products: [{ product_id: selectedProduct.value, quantity: "0" }],
            },
          ],
        };
      }
      if (
        existingStorage.products.some(
          (item) => item.product_id === selectedProduct.value,
        )
      )
        return current;
      return {
        ...current,
        central_storage: current.central_storage.some(
          (item) => item.product_id === selectedProduct.value,
        )
          ? current.central_storage
          : [
              ...current.central_storage,
              { product_id: selectedProduct.value, quantity: "0" },
            ],
        pasture_storage: current.pasture_storage.map((storage) =>
          storage.micro_area_id === microAreaId
            ? {
                ...storage,
                products: [
                  ...storage.products,
                  { product_id: selectedProduct.value, quantity: "0" },
                ],
              }
            : storage,
        ),
      };
    });
    setPendingProducts((current) => ({ ...current, [storageKey]: null }));
  }

  function getAvailableProducts(stockProducts: StockProduct[]) {
    const usedIds = new Set(stockProducts.map((item) => item.product_id));
    return productOptions.filter((option) => !usedIds.has(option.value));
  }

  function validateInventory() {
    const stockProducts = [
      ...inventory.central_storage,
      ...inventory.pasture_storage.flatMap((storage) => storage.products),
    ];
    return stockProducts.every(
      (item) =>
        item.quantity !== "" &&
        Number.isFinite(Number(item.quantity)) &&
        Number(item.quantity) >= 0,
    );
  }

  function saveInventory() {
    if (!selectedFarm || saving) return;
    if (!validateInventory()) {
      return;
    }

    setSaving(true);
    const payload = {
      central_storage: inventory.central_storage.map((item) => ({
        product_id: item.product_id,
        quantity: Number(item.quantity),
      })),
      pasture_storage: inventory.pasture_storage
        .filter((storage) => storage.products.length > 0)
        .map((storage) => ({
          micro_area_id: storage.micro_area_id,
          products: storage.products.map((item) => ({
            product_id: item.product_id,
            quantity: Number(item.quantity),
          })),
        })),
    };

    axios
      .patch(`/farms/${selectedFarm.value}/inventory`, payload)
      .then(() => {
        const inventoryWithoutZero = removeZeroQuantityProducts(inventory);
        setInventory(inventoryWithoutZero);
        setInitialInventory(inventoryWithoutZero);
        setEnabledPastures(
          inventoryWithoutZero.pasture_storage.map(
            (storage) => storage.micro_area_id,
          ),
        );
        setPendingProducts({});
        setSuccessVisible(true);
      })
      .finally(() => setSaving(false));
  }

  function cancelChanges() {
    setSystemManager(null);
  }

  function renderProducts(stockProducts: StockProduct[], microAreaId?: number) {
    if (!stockProducts.length)
      return (
        <p className="stock-management__empty">
          Nenhum produto neste depósito.
        </p>
      );
    return (
      <div className="stock-management__products">
        <div className="stock-management__product-header" aria-hidden="true">
          <span>Produto</span>
          <span>Quantidade</span>
        </div>
        {stockProducts.map((item) => {
          const product = products.find(
            (catalogProduct) => catalogProduct.id === item.product_id,
          );
          return (
            <div
              className="stock-management__product-row"
              key={item.product_id}
            >
              <div>
                <strong>
                  {product?.trade_name || `Produto #${item.product_id}`}
                </strong>
                {product?.classification && (
                  <small>{product.classification}</small>
                )}
              </div>
              <InputV2
                name={`quantity-${microAreaId ?? "central"}-${item.product_id}`}
                type="number"
                value={item.quantity}
                onChange={(value) =>
                  microAreaId === undefined
                    ? updateCentralQuantity(item.product_id, value)
                    : updatePastureQuantity(microAreaId, item.product_id, value)
                }
                suffix="kg"
                anchor="end"
              />
            </div>
          );
        })}
      </div>
    );
  }

  function renderAddProduct(
    storageKey: string,
    stockProducts: StockProduct[],
    microAreaId?: number,
  ) {
    const availableProducts = getAvailableProducts(stockProducts);
    if (!availableProducts.length)
      return (
        <small className="stock-management__all-added">
          Todos os produtos já foram adicionados.
        </small>
      );
    return (
      <div className="stock-management__add-product">
        <div style={{ maxWidth: 350 }}>
          <DropdownV2
            name={`product-${storageKey}`}
            value={pendingProducts[storageKey] || null}
            options={availableProducts}
            placeholder="Selecione um produto"
            searchable
            menuMount="overlay"
            onChange={(option) =>
              setPendingProducts((current) => ({
                ...current,
                [storageKey]: option,
              }))
            }
          />
        </div>
        <ButtonOnlyTitle
          title="Adicionar"
          theme="light"
          disabled={!pendingProducts[storageKey]}
          onClick={() => addProduct(storageKey, microAreaId)}
        />
      </div>
    );
  }

  return (
    <div className="stock-management">
      {mobileHeaderAction && <div className="system-manager__mobile-content-action">{mobileHeaderAction}</div>}
      <div className="stock-management__scroll">
        {loading && (
          <p className="stock-management__status">Carregando estoque...</p>
        )}

        {!selectedFarm && !loading && (
          <div className="stock-management__placeholder">
            <strong>Selecione uma fazenda</strong>
            <span>Os depósitos central e dos pastos serão exibidos aqui.</span>
          </div>
        )}

        {selectedFarm && !loading && (
          <div className="stock-management__content">
            <section className="stock-management__storage stock-management__storage--central">
              <div className="stock-management__storage-title">
                <div>
                  <small>Depósito central</small>
                  <h3>{selectedFarm.label}</h3>
                </div>
                <span>
                  {inventory.central_storage.length}{" "}
                  {inventory.central_storage.length === 1
                    ? "produto"
                    : "produtos"}
                </span>
              </div>
              {renderProducts(inventory.central_storage)}
              {renderAddProduct("central", inventory.central_storage)}
            </section>

            <div className="stock-management__pastures-heading">
              <h3>Depósitos dos pastos</h3>
              <span>
                {displayedPastures.length}{" "}
                {displayedPastures.length === 1 ? "depósito" : "depósitos"}
              </span>
            </div>

            {!allPastures.length && (
              <p className="stock-management__status">
                Esta fazenda não possui pastos cadastrados.
              </p>
            )}

            <div className="stock-management__pastures">
              {displayedPastures.map((microArea) => {
                const microAreaId = Number(microArea.id);
                const storage = inventory.pasture_storage.find(
                  (item) => item.micro_area_id === microAreaId,
                );
                const stockProducts = storage?.products || [];
                return (
                  <section
                    className="stock-management__storage"
                    key={microAreaId}
                  >
                    <div className="stock-management__storage-title">
                      <div>
                        <small>Pasto</small>
                        <h3>{getMicroAreaLabel(microArea)}</h3>
                      </div>
                      <span>
                        {stockProducts.length}{" "}
                        {stockProducts.length === 1 ? "produto" : "produtos"}
                      </span>
                    </div>
                    {renderProducts(stockProducts, microAreaId)}
                    {renderAddProduct(
                      `pasture-${microAreaId}`,
                      stockProducts,
                      microAreaId,
                    )}
                  </section>
                );
              })}

              {pastureOptions.length > 0 && (
                <section className="stock-management__storage stock-management__storage--new">
                  <div className="stock-management__storage-title">
                    <div>
                      <small>Pasto sem produtos</small>
                      <h3>Novo depósito</h3>
                    </div>
                  </div>
                  <div className="stock-management__add-storage">
                    <div style={{ maxWidth: 350 }}>
                      <DropdownV2
                        name="new-pasture-storage"
                        value={pendingPasture}
                        options={pastureOptions}
                        placeholder="Selecione um pasto"
                        searchable
                        menuMount="overlay"
                        onChange={setPendingPasture}
                      />
                    </div>

                    <ButtonOnlyTitle
                      title="Adicionar"
                      theme="light"
                      disabled={!pendingPasture}
                      onClick={addPastureStorage}
                    />
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </div>

      <FooterEditOnboarding
        isNew
        undoFunction={cancelChanges}
        saveFunction={saveInventory}
        disabledSubmit={saving}
        disabledSave={!selectedFarm || loading}
      />

      <AlertModal
        visible={successVisible}
        contentStyle={{
          width: 440,
          height: 240,
          borderRadius: 20,
          padding: 30,
        }}
      >
        <section className="stock-management__success-modal">
          <CheckIcon width={42} height={42} />
          <div>
            <TitleModal>Concluído!</TitleModal>
            <TextModal>Estoque salvo com sucesso.</TextModal>
          </div>
          <ButtonOnlyTitle
            title="Fechar"
            theme="info"
            onClick={() => setSuccessVisible(false)}
          />
        </section>
      </AlertModal>
    </div>
  );
}

function getMicroAreaLabel(microArea: MicroArea) {
  const prefix = [microArea.farm?.initials, microArea.area?.initials]
    .filter(Boolean)
    .join("-");
  const initials = microArea.initials || `Pasto #${microArea.id}`;
  return prefix ? `${prefix}-${initials}` : initials;
}
